import { createAppUrlFromConfig, createDomainConfig, navigateToAppUrlFromConfig } from '@taxfix/scouts-helpers'

/**
 * Retrieves domain config for the app
 *
 * Creates and returns a domain config object using env variables
 * and browser window location data (when available)
 * The config is used for URL handling and routing throughout the app
 */
export const getDomainConfig = () => {
    // For Vercel preview deployments, use the current hostname if available
    // This ensures redirect URIs use the actual URL being visited
    const getVercelUrl = () => {
        if (typeof window !== 'undefined' && window.location.hostname.endsWith('.vercel.app')) {
            return window.location.hostname
        }
        return import.meta.env.VERCEL_URL
    }

    const config = createDomainConfig({
        baseUrl: import.meta.env.REACT_APP_BASE_URL,
        vercelUrl: getVercelUrl(),
        baseLocalePrefix: import.meta.env.REACT_APP_BASE_LOCALE_PREFIX,
        appBaseNamePath: import.meta.env.REACT_APP_BASENAME,
        pathname: typeof window !== 'undefined' ? window.location.pathname : '',
    })

    return config
}

/**
 * Creates a URL containing the path relative to the app basename (and locale prefix if applicable)
 * @example
 * getAppUrl('/overview') // Returns: '/assessment/overview' or '/en-uk/assessment/overview'
 */
export const getAppUrl = (path: string) => {
    const config = getDomainConfig()
    return createAppUrlFromConfig(path, config)
}

/**
 * Creates a URL containing the path relative to the app basename and navigates to it
 */
export const navigateToApp = (path: string) => {
    const config = getDomainConfig()
    return navigateToAppUrlFromConfig(path, config)
}

/**
 * Retrieves the full base URL (ie domain + basename) for API callbacks and redirects
 * @example
 * getBaseUrl() // In Prod, returns: 'https://taxscouts.com/assessment/' or 'https://taxfix.com/en-uk/assessment/'
 */
export const getBaseUrl = () => {
    const config = getDomainConfig()
    return config.baseUrl
}

/**
 * Returns the full base path including locale prefix (if available) and app basename
 * @example
 * getFullBasenamePath() // Returns: '/assessment/' or '/en-uk/assessment/'
 */
export const getFullBasenamePath = () => {
    const config = getDomainConfig()
    return config.appBaseNamePath
}
